import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPendingDealDoc, PENDING_DEAL_EVENT, dealAwareUrl } from '../services/branch';
import { Deal } from '../types/deal';
import { useFirebaseImageUrl } from '../hooks/useFirebaseImageUrl';
import { getPageImage, getPageLayout, layoutToStyle } from '../utils/dealImages';
import { useAwaitingDeal } from '../hooks/useAwaitingDeal';
import { sendVerificationEmail } from '../services/emailVerification';
import CoBrandHeader from './CoBrandHeader';
import CalloutCard from './CalloutCard';
import LanguageSwitcher from './LanguageSwitcher';
import { t } from '../i18n';
import './Auth.css';
import './Promo.css';


const VerifyEmail: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingDeal, setPendingDeal] = useState<Deal | null>(null);
  const [feedback, setFeedback] = useState('');
  const [resending, setResending] = useState(false);
  const productImage = useFirebaseImageUrl(getPageImage(pendingDeal, 'verify'));
  const [heroLoaded, setHeroLoaded] = useState(false);
  const awaitingDeal = useAwaitingDeal(productImage, heroLoaded);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (currentUser.emailVerified) {
      navigate('/offers');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const sync = () => {
      const deal = getPendingDealDoc();
      if (deal) setPendingDeal(deal);
    };
    sync();
    window.addEventListener(PENDING_DEAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_DEAL_EVENT, sync);
  }, []);

  // Poll for verification by reloading the Firebase user (no re-auth)
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(async () => {
      try {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          clearInterval(interval);
          navigate('/offers');
        }
      } catch (err) {
        console.error('[VerifyEmail] Error checking verification:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentUser, navigate]);

  const handleResend = async () => {
    if (!currentUser?.email) return;
    try {
      setResending(true);
      setFeedback('');
      const result = await sendVerificationEmail(currentUser.email, dealAwareUrl('/verify-email'));
      setFeedback(result.success ? t('verifyEmail.resent') : result.error || t('verifyEmail.failedToResend'));
    } catch (err: any) {
      setFeedback(t('verifyEmail.failedToResend'));
    } finally {
      setResending(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="hp-page">
      <LanguageSwitcher />
      <div className="hp-split">
        <div className={`hp-hero${awaitingDeal ? ' hp-hero--awaiting' : ''}`}>
          {productImage && (
            <img src={productImage} alt={pendingDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(pendingDeal, 'verify'))} onLoad={() => setHeroLoaded(true)} />
          )}
        </div>

        <div className="hp-panel">
          <div className="hp-panel-inner">
            <CoBrandHeader deal={pendingDeal} />

            <CalloutCard deal={pendingDeal} page="verify" />

            <div className="hp-success">
              <p className="hp-success-lead">{t('promo.successLead')}</p>
              <div className="hp-success-card">
                <span>{t('verifyEmail.heading')}</span>
              </div>
              <p className="hp-success-sub">
                {t('verifyEmail.sentTo')} <strong>{currentUser?.email}</strong>
                <br />
                {t('verifyEmail.checkInbox')}
              </p>
              <p className="hp-subtitle">{t('verifyEmail.checkingStatus')}</p>
            </div>

            {feedback && <div className="error-message">{feedback}</div>}

            <button onClick={handleResend} disabled={resending} className="hp-cta">
              {resending ? t('verifyEmail.resending') : t('verifyEmail.resend')}
            </button>

            <p className="hp-secondary-link">
              <button onClick={handleUseDifferentAccount}>{t('verifyEmail.useDifferentAccount')}</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
